import "./images-to-pdf.css";
import { fileListToArray } from "./file-list-core.js";
import {
  IMAGE_LIMITS,
  QUALITY_PRESETS,
  detectImageType,
  moveItem,
  normalizePdfName,
  normalizeRotation,
  rotatedImageSize,
  validateCollection,
  validateImageFile
} from "./image-pdf-core.js";
import { createImagePdf } from "./image-pdf-export.js";
import { nextScannerFilter, scannerCropFromInsets, scannerCropInsets, scannerFilterCss } from "./pdf-scanner-core.js";
import { createOperationResult } from "./operation-result.js";
import { bmpCanvas, canvasBlob, decodeBmp } from "./bmp-browser.js";

const root = document.querySelector("[data-image-pdf]");

if (root) {
  const scannerMode = root.dataset.imagePdfMode === "scanner";
  const ids = [
    "imageStatus", "imageUploadPanel", "imageDropZone", "chooseImagesButton", "imageFileInput",
    "imageWorkspace", "imageCount", "addMoreImages", "clearImages", "createPdf", "processingProgress",
    "imageList", "outputName", "pageSize", "orientationField", "pageOrientation", "pageMargin", "imageQuality",
    "scannerCropDialog", "scannerCropCanvas", "scannerCropTop", "scannerCropRight", "scannerCropBottom", "scannerCropLeft", "saveScannerCrop"
  ];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const state = { items: [], busy: false, draggedId: null, nextId: 1, scannerCropItem: null, scannerCropBitmap: null };
  const operationResult = createOperationResult(root, elements.imageWorkspace);

  function showStatus(message, kind = "error") {
    elements.imageStatus.textContent = message;
    elements.imageStatus.dataset.kind = kind;
    elements.imageStatus.hidden = !message;
  }

  function clearStatus() {
    elements.imageStatus.hidden = true;
    elements.imageStatus.textContent = "";
  }

  function setBusy(busy, label = "") {
    state.busy = busy;
    for (const control of [elements.addMoreImages, elements.clearImages, elements.createPdf]) control.disabled = busy;
    elements.createPdf.textContent = busy ? label || "Working…" : "Create PDF";
    updateProcessingUi();
  }

  async function addFiles(fileList) {
    const files = fileListToArray(fileList);
    if (!files.length || state.busy) return;
    clearStatus();
    const rejected = [];
    const pending = [];

    for (const file of files) {
      const collectionError = validateCollection(state.items, [{ size: file.size }]);
      if (collectionError) { rejected.push(`${file.name}: ${collectionError}`); continue; }
      const item = {
        id: `image-${state.nextId++}`,
        name: file.name,
        size: file.size,
        type: null,
        blob: file,
        previewUrl: URL.createObjectURL(file),
        width: 0,
        height: 0,
        rotation: 0,
        filter: "original",
        crop: { x: 0, y: 0, width: 1, height: 1 },
        status: "pending"
      };
      state.items.push(item);
      pending.push(item);
    }

    if (state.items.length) openWorkspace();
    renderItems();
    elements.imageFileInput.value = "";
    await new Promise(requestAnimationFrame);
    await processWithConcurrency(pending, 2, async item => {
      try {
        const header = new Uint8Array(await item.blob.slice(0, 20).arrayBuffer());
        const type = detectImageType(header);
        const fileError = validateImageFile(item.blob, type);
        if (fileError) throw new Error(fileError);
        if (type === "bmp") {
          const decoded = decodeBmp(new Uint8Array(await item.blob.arrayBuffer()), { maxPixels: IMAGE_LIMITS.pixels });
          const prepared = await canvasBlob(bmpCanvas(decoded), "image/png");
          URL.revokeObjectURL(item.previewUrl);
          item.blob = prepared;
          item.previewUrl = URL.createObjectURL(prepared);
          item.type = "png";
          item.width = decoded.width;
          item.height = decoded.height;
          item.status = "ready";
          return;
        }
        const bitmap = await createImageBitmap(item.blob, { imageOrientation: "from-image" });
        if (bitmap.width * bitmap.height > IMAGE_LIMITS.pixels) {
          bitmap.close();
          throw new Error("Decoded image exceeds the 64 megapixel limit.");
        }
        if (!state.items.includes(item)) { bitmap.close(); return; }
        item.type = type;
        item.width = bitmap.width;
        item.height = bitmap.height;
        item.status = "ready";
        bitmap.close();
      } catch (error) {
        if (!state.items.includes(item)) return;
        URL.revokeObjectURL(item.previewUrl);
        state.items.splice(state.items.indexOf(item), 1);
        rejected.push(`${item.name}: ${error?.message || "Could not decode image."}`);
      } finally {
        renderItems();
      }
    });

    renderItems();
    if (rejected.length) showStatus(`Some images were skipped. ${rejected.join(" ")}`);
  }

  async function processWithConcurrency(items, concurrency, task) {
    let cursor = 0;
    const worker = async () => {
      while (cursor < items.length) {
        const item = items[cursor++];
        await task(item);
      }
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker));
  }

  function updateProcessingUi() {
    const pending = state.items.filter(item => item.status === "pending").length;
    const ready = state.items.length - pending;
    elements.processingProgress.hidden = pending === 0;
    elements.processingProgress.textContent = `Ready ${ready} of ${state.items.length}`;
    elements.createPdf.disabled = state.busy || pending > 0 || ready === 0;
  }

  function openWorkspace() {
    elements.imageUploadPanel.hidden = true;
    elements.imageWorkspace.hidden = false;
    root.classList.add("is-arranging");
    document.body.classList.add("image-arranger-active");
  }

  function closeWorkspace() {
    elements.imageWorkspace.hidden = true;
    elements.imageUploadPanel.hidden = false;
    root.classList.remove("is-arranging");
    document.body.classList.remove("image-arranger-active");
  }

  function renderItems() {
    elements.imageCount.textContent = String(state.items.length);
    elements.imageList.replaceChildren();
    state.items.forEach((item, index) => elements.imageList.append(createCard(item, index)));
    if (!state.items.length) closeWorkspace();
    updateProcessingUi();
  }

  function createCard(item, index) {
    const card = document.createElement("article");
    card.className = "image-card";
    if (item.status === "pending") card.classList.add("is-pending");
    card.draggable = true;
    card.dataset.id = item.id;
    card.setAttribute("aria-label", `Page ${index + 1}: ${item.name}`);

    const preview = document.createElement("div");
    preview.className = "image-preview";
    const image = document.createElement("img");
    image.src = item.previewUrl;
    image.alt = "";
    image.style.transform = `rotate(${item.rotation}deg)`;
    image.style.filter = scannerFilterCss(item.filter);
    preview.append(image);

    const badge = document.createElement("span");
    badge.className = "page-badge";
    badge.textContent = String(index + 1);

    const details = document.createElement("div");
    details.className = "image-details";
    const name = document.createElement("strong");
    name.title = item.name;
    name.textContent = item.name;
    const size = document.createElement("span");
    const rotated = rotatedImageSize(item.width, item.height, item.rotation);
    size.textContent = item.status === "pending" ? "Preparing preview…" : `${rotated.width} × ${rotated.height} px${scannerMode ? ` · ${item.filter.replace("-", " ")}` : ""}`;
    details.append(name, size);

    const actions = document.createElement("div");
    actions.className = "image-card-actions";
    actions.append(
      actionButton("←", "Move page earlier", "previous", index === 0),
      actionButton("→", "Move page later", "next", index === state.items.length - 1),
      actionButton("↶", "Rotate left", "rotate-left"),
      actionButton("↷", "Rotate right", "rotate-right")
    );
    if (scannerMode) actions.append(actionButton("◐", "Change page filter", "filter"), actionButton("⌗", "Crop page", "crop"));
    actions.append(actionButton("×", "Remove image", "remove"));
    if (scannerMode) actions.classList.add("scanner-card-actions");
    actions.addEventListener("click", event => handleCardAction(event, item.id));

    card.addEventListener("dragstart", event => {
      state.draggedId = item.id;
      card.classList.add("is-dragging");
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", item.id);
    });
    card.addEventListener("dragend", () => {
      state.draggedId = null;
      document.querySelectorAll(".image-card").forEach(value => value.classList.remove("is-dragging", "is-drop-target"));
    });
    card.addEventListener("dragover", event => { event.preventDefault(); card.classList.add("is-drop-target"); });
    card.addEventListener("dragleave", () => card.classList.remove("is-drop-target"));
    card.addEventListener("drop", event => {
      event.preventDefault();
      const sourceId = state.draggedId || event.dataTransfer.getData("text/plain");
      reorderById(sourceId, item.id);
    });
    card.append(preview, badge, details, actions);
    return card;
  }

  function actionButton(text, label, action, disabled = false) {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = text;
    button.title = label;
    button.setAttribute("aria-label", label);
    button.dataset.action = action;
    button.disabled = disabled;
    return button;
  }

  function handleCardAction(event, id) {
    const action = event.target.closest("button")?.dataset.action;
    if (!action) return;
    const index = state.items.findIndex(item => item.id === id);
    if (action === "previous") state.items = moveItem(state.items, index, index - 1);
    if (action === "next") state.items = moveItem(state.items, index, index + 1);
    if (action === "rotate-left") state.items[index].rotation = normalizeRotation(state.items[index].rotation - 90);
    if (action === "rotate-right") state.items[index].rotation = normalizeRotation(state.items[index].rotation + 90);
    if (action === "filter") state.items[index].filter = nextScannerFilter(state.items[index].filter);
    if (action === "crop") { openScannerCrop(state.items[index]); return; }
    if (action === "remove") {
      URL.revokeObjectURL(state.items[index].previewUrl);
      state.items.splice(index, 1);
    }
    renderItems();
  }

  function reorderById(sourceId, targetId) {
    const from = state.items.findIndex(item => item.id === sourceId);
    const to = state.items.findIndex(item => item.id === targetId);
    state.items = moveItem(state.items, from, to);
    renderItems();
  }

  async function prepareImage(item, qualityName) {
    const preset = QUALITY_PRESETS[qualityName] || QUALITY_PRESETS.HIGH;
    const bitmap = await createImageBitmap(item.blob, { imageOrientation: "from-image" });
    const crop = item.crop || { x: 0, y: 0, width: 1, height: 1 };
    const cropWidth = Math.max(1, Math.round(bitmap.width * crop.width));
    const cropHeight = Math.max(1, Math.round(bitmap.height * crop.height));
    const scale = Number.isFinite(preset.maxEdge) ? Math.min(1, preset.maxEdge / Math.max(cropWidth, cropHeight)) : 1;
    const sourceWidth = Math.max(1, Math.round(cropWidth * scale));
    const sourceHeight = Math.max(1, Math.round(cropHeight * scale));
    const rotated = rotatedImageSize(sourceWidth, sourceHeight, item.rotation);
    const canvas = document.createElement("canvas");
    canvas.width = rotated.width;
    canvas.height = rotated.height;
    const context = canvas.getContext("2d", { alpha: true });
    const preservePng = item.type === "png" && qualityName !== "COMPACT";
    if (!preservePng) {
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    context.filter = scannerFilterCss(item.filter);
    applyCanvasRotation(context, item.rotation, canvas.width, canvas.height);
    context.drawImage(bitmap, Math.round(bitmap.width * crop.x), Math.round(bitmap.height * crop.y), cropWidth, cropHeight, 0, 0, sourceWidth, sourceHeight);
    bitmap.close();
    const mime = preservePng ? "image/png" : "image/jpeg";
    const blob = await canvasBlob(canvas, mime, preset.jpegQuality);
    return { bytes: new Uint8Array(await blob.arrayBuffer()), type: preservePng ? "png" : "jpeg", width: canvas.width, height: canvas.height };
  }

  async function openScannerCrop(item) {
    state.scannerCropBitmap?.close();
    state.scannerCropItem = item;
    state.scannerCropBitmap = await createImageBitmap(item.blob, { imageOrientation: "from-image" });
    const insets = scannerCropInsets(item.crop);
    for (const side of ["Top", "Right", "Bottom", "Left"]) {
      elements[`scannerCrop${side}`].value = String(insets[side.toLowerCase()]);
      elements[`scannerCrop${side}`].parentElement.querySelector("output").value = `${insets[side.toLowerCase()]}%`;
    }
    drawScannerCropPreview();
    elements.scannerCropDialog.showModal();
  }

  function scannerCropValues() {
    return Object.fromEntries(["top", "right", "bottom", "left"].map(side => [side, Number(elements[`scannerCrop${side[0].toUpperCase()}${side.slice(1)}`].value)]));
  }

  function drawScannerCropPreview() {
    const bitmap = state.scannerCropBitmap;
    if (!bitmap) return;
    const canvas = elements.scannerCropCanvas;
    const scale = Math.min(1, 640 / bitmap.width, 480 / bitmap.height);
    canvas.width = Math.max(1, Math.round(bitmap.width * scale)); canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext("2d"); context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const crop = scannerCropFromInsets(scannerCropValues());
    context.fillStyle = "rgba(18,24,40,.55)";
    context.fillRect(0, 0, canvas.width, crop.y * canvas.height);
    context.fillRect(0, (crop.y + crop.height) * canvas.height, canvas.width, canvas.height);
    context.fillRect(0, crop.y * canvas.height, crop.x * canvas.width, crop.height * canvas.height);
    context.fillRect((crop.x + crop.width) * canvas.width, crop.y * canvas.height, canvas.width, crop.height * canvas.height);
    context.strokeStyle = "#5b4ce6"; context.lineWidth = 3; context.strokeRect(crop.x * canvas.width, crop.y * canvas.height, crop.width * canvas.width, crop.height * canvas.height);
    for (const side of ["Top", "Right", "Bottom", "Left"]) elements[`scannerCrop${side}`].parentElement.querySelector("output").value = `${scannerCropValues()[side.toLowerCase()]}%`;
  }

  function saveScannerCrop() {
    if (state.scannerCropItem) state.scannerCropItem.crop = scannerCropFromInsets(scannerCropValues());
    state.scannerCropBitmap?.close(); state.scannerCropBitmap = null; state.scannerCropItem = null;
    elements.scannerCropDialog.close(); renderItems();
  }

  function applyCanvasRotation(context, rotation, width, height) {
    const value = normalizeRotation(rotation);
    if (value === 90) { context.translate(width, 0); context.rotate(Math.PI / 2); }
    if (value === 180) { context.translate(width, height); context.rotate(Math.PI); }
    if (value === 270) { context.translate(0, height); context.rotate(-Math.PI / 2); }
  }

  function canvasBlob(canvas, type, quality) {
    return new Promise((resolve, reject) => canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error("The browser could not encode an image.")),
      type,
      quality
    ));
  }

  async function downloadPdf() {
    if (!state.items.length || state.busy || state.items.some(item => item.status === "pending")) return;
    clearStatus();
    setBusy(true, "Creating…");
    elements.processingProgress.hidden = false;
    try {
      const options = {
        pageSize: elements.pageSize.value,
        orientation: elements.pageOrientation.value,
        margin: Number(elements.pageMargin.value),
        quality: elements.imageQuality.value
      };
      const bytes = await createImagePdf(state.items, options, prepareImage, (current, total) => {
        elements.processingProgress.textContent = `Processing ${current} of ${total}`;
      });
      operationResult.show({
        blob: new Blob([bytes], { type: "application/pdf" }),
        fileName: normalizePdfName(elements.outputName.value),
        message: scannerMode ? "Your scanned PDF was created successfully." : "Your PDF was created successfully.",
        details: [`${state.items.length} ${state.items.length === 1 ? "page" : "pages"}`]
      });
    } catch (error) {
      showStatus(error?.message || "smallpdf.app could not create the PDF.");
    } finally {
      elements.processingProgress.hidden = true;
      setBusy(false);
    }
  }

  function clearAll() {
    if (state.items.length && !window.confirm("Remove all images and start over?")) return;
    state.items.forEach(item => URL.revokeObjectURL(item.previewUrl));
    state.items = [];
    renderItems();
  }

  elements.chooseImagesButton.addEventListener("click", event => { event.stopPropagation(); elements.imageFileInput.click(); });
  elements.addMoreImages.addEventListener("click", () => elements.imageFileInput.click());
  elements.imageFileInput.addEventListener("change", () => addFiles(elements.imageFileInput.files));
  elements.imageDropZone.addEventListener("click", event => { if (event.target !== elements.chooseImagesButton) elements.imageFileInput.click(); });
  elements.imageDropZone.addEventListener("keydown", event => {
    if (event.key === "Enter" || event.key === " ") { event.preventDefault(); elements.imageFileInput.click(); }
  });
  for (const name of ["dragenter", "dragover"]) {
    elements.imageDropZone.addEventListener(name, event => { event.preventDefault(); elements.imageDropZone.classList.add("is-dragging"); });
  }
  for (const name of ["dragleave", "drop"]) {
    elements.imageDropZone.addEventListener(name, event => { event.preventDefault(); elements.imageDropZone.classList.remove("is-dragging"); });
  }
  elements.imageDropZone.addEventListener("drop", event => addFiles(event.dataTransfer.files));
  elements.clearImages.addEventListener("click", clearAll);
  elements.createPdf.addEventListener("click", downloadPdf);
  elements.pageSize.addEventListener("change", () => { elements.orientationField.hidden = elements.pageSize.value === "FIT"; });
  if (scannerMode) {
    for (const side of ["Top", "Right", "Bottom", "Left"]) elements[`scannerCrop${side}`].addEventListener("input", drawScannerCropPreview);
    elements.saveScannerCrop.addEventListener("click", saveScannerCrop);
    elements.scannerCropDialog.addEventListener("close", () => { state.scannerCropBitmap?.close(); state.scannerCropBitmap = null; state.scannerCropItem = null; });
  }
  document.querySelectorAll("[data-image-back]").forEach(link => link.addEventListener("click", event => {
    if (state.items.length && !window.confirm("Leave this tool and discard the arranged images?")) event.preventDefault();
  }));
  window.addEventListener("beforeunload", event => {
    if (!state.items.length || operationResult.isVisible) return;
    event.preventDefault();
    event.returnValue = "";
  });
  window.addEventListener("pagehide", () => { operationResult.dispose(); state.items.forEach(item => URL.revokeObjectURL(item.previewUrl)); });
}
